import { useState } from 'react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'

import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import GenericFormError from 'app/frontend/base/components/GenericFormError'
import { displayErrorToast } from 'app/frontend/base/helpers'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { updateMember } from 'app/frontend/base/api/teamMember'
import { useQueryClient } from '@tanstack/react-query'
import { useTeam } from 'app/frontend/base/config/context/team'

const formSchema = z.object({
  note: z.string().max(2000),
})

interface Props {
  onClose: () => void
  member: wowaudit.TeamMembership
}

const MemberNoteModal = ({ onClose, member }: Props) => {
  const { team } = useTeam()
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string>()
  const { t } = useTranslation()
  const queryClient = useQueryClient()

  const form = useForm<z.infer<typeof formSchema> & { genericError?: string }>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      note: member.note ?? '',
    },
  })

  const onSubmit = (values: z.infer<typeof formSchema>) => {
    setError(undefined)
    setSubmitting(true)
    updateMember(member.teamId, member.id, values)
      .then(async (response) => {
        toast.success(
          t('dashboard.team.roster.member-updated', {
            name: member.characterReference.name,
          }),
        )
        queryClient.setQueryData(['team', { path: team.path }], response)
        onClose()
        setSubmitting(false)
      })
      .catch((response) => {
        displayErrorToast(response, t)
        setSubmitting(false)
        setError(response.response.data.message)
      })
  }

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {t('dashboard.team.roster.edit-note-title', {
              name: member.characterReference.name,
            })}
          </DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} id='edit-note-form'>
            <div className='flex gap-2'>
              <FormField
                control={form.control}
                name='note'
                render={({ field }) => (
                  <FormItem className='grow'>
                    <FormLabel>Note</FormLabel>
                    <FormControl>
                      <Input placeholder='Note' {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <GenericFormError customError={error} />
          </form>
        </Form>

        <DialogFooter>
          <DialogClose asChild>
            <Button type='button' variant='secondary'>
              Cancel
            </Button>
          </DialogClose>
          <Button type='submit' form='edit-note-form' loading={submitting}>
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default MemberNoteModal
